import { useEffect, useState } from 'react';

import { api } from '../../services/api';
import useAuth from '../../hooks/useAuth';
import type {
  AccountSummary,
  AtaHealthReport,
  AtaMemoryResponse,
  AtaSensitiveConsentResponse,
  AtaSensitiveCategory,
  AtaPredicateProposal,
  AtaTuningResponse,
  StyleProfile,
} from '../../types';
import { AtaMemorySection as AtaMemoryCard, AtaSensitiveSection, AtaPlatformOwnerSection } from './AtaMemorySections';

type Props = {
  styleProfile: StyleProfile | null;
  onReload: () => void;
};

const learningStages = [
  { min: 0, max: 19, label: 'Beginner', icon: '🌱' },
  { min: 20, max: 39, label: 'Leerling', icon: '📚' },
  { min: 40, max: 59, label: 'Gevorderd', icon: '⚡' },
  { min: 60, max: 79, label: 'Expert', icon: '🎯' },
  { min: 80, max: 99, label: 'Meester', icon: '🧠' },
  { min: 100, max: 100, label: 'Volledig Geleerd', icon: '🤖' },
];

const getStage = (level: number) =>
  learningStages.find((stage) => level >= stage.min && level <= stage.max) ??
  learningStages[0]!;

type AtaSection = 'memory' | 'reports' | 'proposals' | 'tuning';
const sectionLabels: Record<AtaSection, string> = {
  memory: 'Geheugen',
  reports: 'Dagrapport',
  proposals: 'Predicate-voorstellen',
  tuning: 'Tuning-log',
};

type AtaMemoryPanelProps = {
  styleProfile: StyleProfile | null;
  onReload: () => void;
};

const AtaMemoryPanel = ({ styleProfile, onReload }: AtaMemoryPanelProps) => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [accountId, setAccountId] = useState('');
  const [data, setData] = useState<AtaMemoryResponse | null>(null);
  const [healthReports, setHealthReports] = useState<AtaHealthReport[]>([]);
  const [consentData, setConsentData] = useState<AtaSensitiveConsentResponse | null>(null);
  const [proposals, setProposals] = useState<AtaPredicateProposal[]>([]);
  const [tuningData, setTuningData] = useState<AtaTuningResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<AtaSection>('memory');
  const [learningStatus, setLearningStatus] = useState<{ analyzedCount: number; lastProcessedAt: string | null; pendingCount: number } | null>(null);

  const loadAllData = async (accId: string) => {
    setLoading(true);
    setError(null);
    try {
      const [memoryResult, healthResult, consentResult, proposalsResult, tuningResult] = await Promise.all([
        api.getAtaMemory(accId),
        api.getAtaHealthReports(accId).catch(() => ({ reports: [] })),
        api.getAtaSensitiveConsents(accId).catch(() => null),
        api.getAtaPredicateProposals(accId).catch(() => ({ proposals: [] })),
        api.getAtaTuning(accId).catch(() => null),
      ]);
      setData(memoryResult);
      setHealthReports(healthResult.reports);
      setConsentData(consentResult);
      setProposals(proposalsResult.proposals);
      setTuningData(tuningResult);
    } catch {
      setError('Kon ATA-geheugen niet ophalen.');
    } finally {
      setLoading(false);
    }
  };

  const loadLearningStatus = async (accId: string) => {
    try {
      const status = await api.getAtaLearningStatus(accId);
      setLearningStatus(status);
    } catch {
      setLearningStatus(null);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') return;
    void api.accounts().then((list) => {
      setAccounts(list);
      const active = list.find((a) => a.isActive);
      const defaultId = active?.id ?? list[0]?.id ?? '';
      if (defaultId) {
        setAccountId(defaultId);
        void loadAllData(defaultId);
        void loadLearningStatus(defaultId);
      }
    }).catch(() => undefined);
  }, [user?.role]);

  if (user?.role !== 'admin') return null;

  const handleRevoke = async (relationId: string) => {
    if (!window.confirm('Dit feit intrekken? Dit kan niet ongedaan worden gemaakt.')) return;
    setRevokingId(relationId);
    try {
      await api.invalidateAtaRelation(relationId);
      await loadAllData(accountId.trim());
    } catch {
      setError('Kon feit niet intrekken.');
    } finally {
      setRevokingId(null);
    }
  };

  const handleApproveProposal = async (proposalId: string) => {
    setActioningId(proposalId);
    try {
      await api.approveAtaPredicateProposal(proposalId);
      await loadAllData(accountId.trim());
    } catch {
      setError('Kon voorstel niet goedkeuren.');
    } finally {
      setActioningId(null);
    }
  };

  const handleRejectProposal = async (proposalId: string) => {
    setActioningId(proposalId);
    try {
      await api.rejectAtaPredicateProposal(proposalId);
      await loadAllData(accountId.trim());
    } catch {
      setError('Kon voorstel niet afwijzen.');
    } finally {
      setActioningId(null);
    }
  };

  const handleRollbackTuning = async (logId: string) => {
    if (!window.confirm('Deze tuning-wijziging terugdraaien?')) return;
    setActioningId(logId);
    try {
      const result = await api.rollbackAtaTuning(logId);
      if (result.warning) {
        window.alert(`Waarschuwing: ${result.warning}`);
      }
      await loadAllData(accountId.trim());
    } catch {
      setError('Kon rollback niet uitvoeren.');
    } finally {
      setActioningId(null);
    }
  };

  const handleToggleDefault = async (category: AtaSensitiveCategory, enabled: boolean) => {
    try {
      await api.updateAtaSensitiveDefault(accountId.trim(), category, enabled);
      await loadAllData(accountId.trim());
    } catch {
      setError('Kon instelling niet wijzigen.');
    }
  };

  const handleAnalyze = async () => {
    if (!accountId) return;
    setAnalyzing(true);
    setMessage(null);
    try {
      const profile = await api.analyzeMessages(accountId);
      onReload();
      const added = profile.messagesAnalyzed - (styleProfile?.messagesAnalyzed ?? 0);
      setMessage(
        added > 0
          ? `Analyse voltooid. ${added} bericht${added === 1 ? '' : 'en'} toegevoegd. De AI gebruikt je stijl nu automatisch in automode replies.`
          : added === 0 && profile.messagesAnalyzed === 0
            ? 'Geen uitgaande berichten gevonden om te analyseren.'
            : 'Analyse voltooid. Geen nieuwe berichten om te leren.',
      );
      await loadLearningStatus(accountId);
    } catch {
      setMessage('Analyse mislukt. Controleer of de AI provider is geconfigureerd.');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="ata-memory-section">
      {accounts.length > 0 && (
        <div className="ata-filter-row">
          <select
            value={accountId}
            onChange={(e) => {
              setAccountId(e.target.value);
              if (e.target.value) {
                void loadAllData(e.target.value);
                void loadLearningStatus(e.target.value);
              }
            }}
            className="ata-account-input"
          >
            {accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.label} ({acc.provider})
              </option>
            ))}
          </select>
          <button
            className="button secondary small"
            disabled={analyzing || !accountId}
            onClick={handleAnalyze}
          >
            {analyzing ? 'Analyseren…' : 'Mijn berichten analyseren'}
          </button>
          {loading && <span className="muted small">Laden…</span>}
        </div>
      )}

      {learningStatus && (
        <div className="ata-status-row">
          <span className="muted small">
            Berichten geanalyseerd: {learningStatus.analyzedCount}
          </span>
          <span className="muted small">
            Laatste automatische analyse:{' '}
            {learningStatus.lastProcessedAt
              ? new Date(learningStatus.lastProcessedAt).toLocaleString('nl-NL')
              : 'nog niet'}
          </span>
          <span className="muted small">
            Nog te verwerken: {learningStatus.pendingCount}
          </span>
        </div>
      )}

      {error && <p className="notice warning">{error}</p>}
      {message && <p className="notice">{message}</p>}

      {data && data.truncated && (
        <p className="notice warning">
          Meer dan 500 relaties beschikbaar. Verfijn je filter of gebruik een smaller account.
        </p>
      )}

      <div className="ata-memory-layout">
        <div className="ata-view-cell">
          <label>
            Sectie
            <select
              value={activeSection}
              onChange={(e) => setActiveSection(e.target.value as AtaSection)}
              className="ata-view-select"
            >
              {(Object.keys(sectionLabels) as AtaSection[]).map((key) => (
                <option key={key} value={key}>
                  {sectionLabels[key]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <AtaSensitiveSection consentData={consentData} onToggleDefault={handleToggleDefault} />

        <div className={`ata-selected-content ata-view--${activeSection}`}>
          {activeSection === 'memory' ? (
            <AtaMemoryCard data={data} revokingId={revokingId} onRevoke={handleRevoke} />
          ) : (
            <AtaPlatformOwnerSection
              healthReports={healthReports}
              proposals={proposals}
              tuningData={tuningData}
              actioningId={actioningId}
              onApproveProposal={handleApproveProposal}
              onRejectProposal={handleRejectProposal}
              onRollbackTuning={handleRollbackTuning}
            />
          )}
        </div>
      </div>
    </div>
  );
};


const LearningPanel = ({ styleProfile, onReload }: Props) => {
  const [toneDescription, setToneDescription] = useState(
    styleProfile?.toneDescription ?? '',
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<string | null>(null);

  useEffect(() => {
    if (!styleProfile) return;
    void api.getStyleSuggestion()
      .then(({ suggestion: s }) => setSuggestion(s))
      .catch(() => setSuggestion(null));
  }, [styleProfile]);

  const handleSaveTone = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.updateStyle(toneDescription);
      onReload();
      setMessage('Stijlbeschrijving opgeslagen. De AI past dit toe in automode replies.');
    } catch {
      setMessage('Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const handleApplySuggestion = () => {
    if (suggestion) setToneDescription(suggestion);
  };

  const level = styleProfile?.learningLevel ?? 0;
  const stage = getStage(level);
  const hasStyle = (styleProfile?.messagesAnalyzed ?? 0) > 0 || !!styleProfile?.toneDescription;

  return (
    <section className="learning-panel" aria-label="ATA panel">
      <header className="panel-header">
        <div>
          <p className="eyebrow">AI</p>
          <h2>ATA — Stijl &amp; Geheugen</h2>
        </div>
        <span className={`learning-status-badge ${hasStyle ? 'active' : 'inactive'}`}>
          {hasStyle ? '● Actief' : '● Inactief'}
        </span>
      </header>

      <p className="tool-description">
        De ATA-engine analyseert je uitgaande berichten en leert je stijl.
        Wanneer automode aan staat, gebruikt de AI automatisch je geleerde stijl,
        woordkeuze en toon in automatische replies. Daarnaast extraheert ATA
        feiten uit gesprekken voor cross-contact vraagbeantwoording.
      </p>

      <div className="learning-level">
        <div className="learning-stage">
          <span className="stage-icon" aria-hidden="true">
            {stage.icon}
          </span>
          <div>
            <strong>{stage.label}</strong>
            <span className="muted">{level}% geleerd</span>
          </div>
        </div>
        <div className="progress-bar" role="progressbar" aria-valuenow={level}>
          <div className="progress-fill" style={{ width: `${level}%` }} />
        </div>
      </div>

      {styleProfile && styleProfile.messagesAnalyzed > 0 && (
        <div className="style-stats">
          <div className="stat">
            <span className="muted">Berichten geanalyseerd</span>
            <strong>{styleProfile.messagesAnalyzed}</strong>
          </div>
          <div className="stat">
            <span className="muted">Gemiddelde lengte</span>
            <strong>{styleProfile.avgLength}</strong>
          </div>
          <div className="stat">
            <span className="muted">Emoji ratio</span>
            <strong>{Math.round(styleProfile.emojiRatio * 100)}%</strong>
          </div>
          <div className="stat">
            <span className="muted">Formeel ratio</span>
            <strong>{Math.round(styleProfile.formalRatio * 100)}%</strong>
          </div>
        </div>
      )}

      {styleProfile && styleProfile.commonWords.length > 0 && (
        <div className="style-words">
          <h3>Veelgebruikte woorden</h3>
          <div className="word-tags">
            {styleProfile.commonWords.slice(0, 12).map((word, index) => (
              <span key={index} className="word-tag">{word}</span>
            ))}
          </div>
        </div>
      )}

      <div className="tone-description">
        <label>
          Stijlbeschrijving
          <textarea
            value={toneDescription}
            onChange={(event) => setToneDescription(event.target.value)}
            placeholder="Beschrijf hoe je wilt dat de AI antwoordt —bijv. 'Vriendelijk, zakelijk, kort. Gebruik geen emojis.'"
            rows={3}
            maxLength={1000}
          />
        </label>
        {suggestion && (
          <div className="tone-suggestion-row">
            <p className="muted small">
              <strong>Voorstel op basis van je berichten:</strong>{' '}
              <span className="tone-suggestion-text">{suggestion}</span>
            </p>
            <button
              className="button secondary small"
              type="button"
              onClick={handleApplySuggestion}
            >
              {toneDescription ? 'Voorstel overschrijven' : 'Voorstel invullen'}
            </button>
          </div>
        )}
        <p className="muted small">
          Deze beschrijving wordt als systeeminstructie meegestuurd met elke AI reply in automode.
        </p>
        <div className="button-row">
          <button
            className="button primary small"
            disabled={saving}
            onClick={handleSaveTone}
          >
            {saving ? 'Bezig…' : 'Opslaan'}
          </button>
        </div>
      </div>

      <AtaMemoryPanel styleProfile={styleProfile} onReload={onReload} />

      {message && <p className="notice">{message}</p>}
    </section>
  );
};

export default LearningPanel;
